import React from 'react';
import { VehicleData } from '../types';
import { 
  ArrowLeft, 
  Car, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Ban,
  Banknote,
  FileText,
  Calendar,
  User,
  Hash,
  Palette,
  AlertCircle,
  Gavel,
  Download
} from 'lucide-react';

interface VehicleResultProps {
  data: VehicleData;
  onBack: () => void;
}

const VehicleResult: React.FC<VehicleResultProps> = ({ data, onBack }) => {
  const getStatusConfig = () => {
    switch (data.status) {
      case 'Regular':
        return {
          color: 'emerald',
          icon: CheckCircle,
          text: 'Situação Regular',
          bgClass: 'bg-emerald-500/10 border-emerald-500/30',
          textClass: 'text-emerald-400',
        };
      case 'Roubo/Furto':
        return {
          color: 'red',
          icon: AlertTriangle,
          text: 'ALERTA: Roubo/Furto',
          bgClass: 'bg-red-500/10 border-red-500/30',
          textClass: 'text-red-400',
        };
      case 'Alienação Fiduciária':
        return {
          color: 'yellow',
          icon: AlertCircle,
          text: 'Alienação Fiduciária',
          bgClass: 'bg-yellow-500/10 border-yellow-500/30',
          textClass: 'text-yellow-400',
        };
      case 'Baixado':
        return {
          color: 'slate',
          icon: Ban,
          text: 'Veículo Baixado',
          bgClass: 'bg-slate-500/10 border-slate-500/30',
          textClass: 'text-slate-400',
        };
      default:
        return {
          color: 'slate',
          icon: Shield,
          text: data.status,
          bgClass: 'bg-slate-500/10 border-slate-500/30',
          textClass: 'text-slate-400',
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const totalFines = data.fines.reduce((sum, f) => sum + f.amount, 0);
  const totalDebts = data.debts?.total || totalFines;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>
        
        <button 
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors text-sm"
        >
          <Download className="w-4 h-4" />
          Exportar PDF
        </button>
      </div>

      {/* Main Card */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
        {/* Status Banner */}
        <div className={`p-4 ${statusConfig.bgClass} border-b`}>
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-6 h-6 ${statusConfig.textClass}`} />
            <span className={`font-bold text-lg ${statusConfig.textClass}`}>
              {statusConfig.text}
            </span>
          </div>
        </div>

        {/* Vehicle Header */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800 rounded-xl">
                <Car className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Placa</p>
                <h1 className="text-3xl font-bold font-mono tracking-wider text-white">
                  {data.plate}
                </h1>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-slate-400 text-sm">Veículo</p>
              <p className="text-xl font-bold text-white">{data.make} {data.model}</p>
              <p className="text-slate-400">{data.year}</p>
            </div>
          </div>
        </div>

        {/* Main Info Grid */}
        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Palette className="w-3 h-3" />
              Cor
            </div>
            <p className="font-medium text-white">{data.color}</p>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Calendar className="w-3 h-3" />
              Ano
            </div>
            <p className="font-medium text-white">{data.year}</p>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Hash className="w-3 h-3" />
              Chassi
            </div>
            <p className="font-mono text-sm text-white">{data.chassis}</p>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <FileText className="w-3 h-3" />
              Renavam
            </div>
            <p className="font-mono text-sm text-white">{data.renavam}</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <User className="w-3 h-3" />
              Proprietário
            </div>
            <p className="font-medium text-white">{data.ownerType}</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Shield className="w-3 h-3" />
              Situação
            </div>
            <p className={`font-medium ${statusConfig.textClass}`}>{data.status}</p>
          </div>
        </div>
      </div>

      {/* Debts Section */}
      {(data.debts || data.fines.length > 0) && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center gap-3">
            <Banknote className="w-5 h-5 text-yellow-400" />
            <h2 className="font-bold text-white">Débitos e Multas</h2>
          </div>
          
          <div className="p-6">
            {data.debts && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">IPVA</p>
                  <p className={`text-lg font-bold ${data.debts.ipva > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {data.debts.ipva > 0 ? `R$ ${data.debts.ipva.toFixed(2)}` : 'Pago'}
                  </p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Licenciamento</p>
                  <p className={`text-lg font-bold ${data.debts.licensing > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {data.debts.licensing > 0 ? `R$ ${data.debts.licensing.toFixed(2)}` : 'Pago'}
                  </p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Multas</p>
                  <p className={`text-lg font-bold ${data.debts.fines > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {data.debts.fines > 0 ? `R$ ${data.debts.fines.toFixed(2)}` : 'Sem multas'}
                  </p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Total</p>
                  <p className={`text-lg font-bold ${totalDebts > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    R$ {totalDebts.toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Fines List */}
            {data.fines.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-300 mb-3">Detalhamento de Multas:</p>
                {data.fines.map((fine, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                    <div>
                      <p className="text-sm text-white">{fine.description}</p>
                      <p className="text-xs text-slate-500">{fine.date}</p>
                    </div>
                    <span className="text-red-400 font-medium">
                      R$ {fine.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {data.fines.length === 0 && !data.debts && (
              <div className="text-center py-4 text-slate-400">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p>Nenhum débito encontrado</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Restrictions Section */}
      {data.restrictions && data.restrictions.length > 0 && (
        <div className="bg-slate-900 rounded-2xl border border-red-500/30 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center gap-3 bg-red-500/5">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h2 className="font-bold text-red-400">Restrições Encontradas</h2>
          </div>
          
          <div className="p-6">
            <div className="space-y-2">
              {data.restrictions.map((restriction, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <Ban className="w-5 h-5 text-red-400 shrink-0" />
                  <span className="text-red-300">{restriction}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Auction History */}
      {data.auctionHistory && data.auctionHistory.length > 0 && (
        <div className="bg-slate-900 rounded-2xl border border-yellow-500/30 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center gap-3 bg-yellow-500/5">
            <Gavel className="w-5 h-5 text-yellow-400" />
            <h2 className="font-bold text-yellow-400">Histórico de Leilão</h2>
          </div>
          
          <div className="p-6">
            <div className="space-y-3">
              {data.auctionHistory.map((auction, idx) => (
                <div key={idx} className="p-4 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-white">{auction.reason}</p>
                      <p className="text-sm text-slate-400">{auction.location}</p>
                    </div>
                    <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded">
                      {auction.date}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* API Source */}
      <div className="text-center text-xs text-slate-500">
        Dados obtidos via: {data.apiSource}
      </div>
    </div>
  );
};

export default VehicleResult;
